import { ageOn, bmrMifflinStJeor, tdeeFromBmr } from './bmr';
import { GOAL_ADJUSTMENT, KCAL_PER_G, MIN_FAT_RATIO, PROTEIN_PER_KG } from './config';
import { clamp, round2, round6 } from './math';
import { microTargets, upperLimits } from './micros-efsa';
import type { GoalDirection, GoalPace, Profile, Targets } from './types';

export interface TargetKcalInput {
  tdee: number;
  bmr: number;
  goal: GoalDirection;
  pace: GoalPace;
}

/**
 * Calorías objetivo (SPEC 6.A).
 *
 * El suelo del BMR no es decorativo: por debajo del metabolismo basal la
 * adherencia se hunde y se pierde masa magra. Si el recorte pedido lo cruza,
 * gana el suelo.
 */
export function targetKcal({ tdee, bmr, goal, pace }: TargetKcalInput): number {
  if (!(tdee > 0)) throw new Error('El TDEE debe ser mayor que 0');
  if (!(bmr > 0)) throw new Error('El BMR debe ser mayor que 0');

  const adjusted = tdee * (1 + GOAL_ADJUSTMENT[goal][pace]);
  return round2(Math.max(adjusted, bmr));
}

export interface MacroInput {
  kcal: number;
  weightKg: number;
  proteinPerKg: number;
}

export interface MacroTargets {
  proteinG: number;
  fatG: number;
  carbsG: number;
  /**
   * false cuando la proteína más la grasa mínima ya superan el objetivo
   * calórico. Pasa con pesos altos y objetivos muy bajos; la interfaz debe
   * decirlo en vez de pintar un cero misterioso en carbohidratos.
   */
  feasible: boolean;
}

export function macroTargets({ kcal, weightKg, proteinPerKg }: MacroInput): MacroTargets {
  if (!(kcal > 0)) throw new Error('Las kcal objetivo deben ser mayores que 0');
  if (!(weightKg > 0)) throw new Error('El peso debe ser mayor que 0');

  const perKg = clamp(proteinPerKg, PROTEIN_PER_KG.min, PROTEIN_PER_KG.max);
  const proteinG = round6(weightKg * perKg);
  const proteinKcal = proteinG * KCAL_PER_G.protein;

  const fatKcal = kcal * MIN_FAT_RATIO;
  const fatG = round6(fatKcal / KCAL_PER_G.fat);

  const remaining = kcal - proteinKcal - fatKcal;
  const feasible = remaining >= 0;

  return {
    proteinG,
    fatG,
    carbsG: round6(Math.max(remaining, 0) / KCAL_PER_G.carbs),
    feasible,
  };
}

/**
 * Todo el encadenado: perfil -> BMR -> TDEE -> kcal -> macros -> micros.
 * `onDate` se pasa porque la edad cambia con el tiempo y el motor no lee el
 * reloj.
 */
export function buildTargets(profile: Profile, onDate: string): Targets {
  const ageYears = ageOn(profile.birthDate, onDate);

  const bmr = bmrMifflinStJeor({
    sex: profile.sex,
    weightKg: profile.weightKg,
    heightCm: profile.heightCm,
    ageYears,
  });
  const tdee = tdeeFromBmr(bmr, profile.activity);
  const manual = profile.manualTargets ?? {};
  const calculated = targetKcal({ tdee, bmr, goal: profile.goal, pace: profile.pace });
  // Un objetivo puesto a mano manda, pero el suelo del BMR sigue siendo suelo:
  // la app no ayuda a nadie a comer por debajo de su metabolismo basal.
  const kcal = manual.kcal != null && manual.kcal > 0 ? Math.max(manual.kcal, bmr) : calculated;

  const macros = macroTargets({
    kcal,
    weightKg: profile.weightKg,
    proteinPerKg: profile.proteinPerKg,
  });

  // "El resto, carbohidratos" (SPEC 6.A) también cuando la proteína o la grasa
  // se fijan a mano: si no, los macros suman más kcal que el objetivo.
  const proteinG = positive(manual.proteinG) ?? macros.proteinG;
  const fatG = positive(manual.fatG) ?? macros.fatG;
  const restKcal = kcal - proteinG * KCAL_PER_G.protein - fatG * KCAL_PER_G.fat;

  return {
    bmr,
    tdee,
    kcal,
    proteinG,
    fatG,
    carbsG: positive(manual.carbsG) ?? round6(Math.max(restKcal, 0) / KCAL_PER_G.carbs),
    micros: microTargets({ sex: profile.sex, ageYears, kcal }),
    upperLimits: upperLimits(kcal),
  };
}

/** Un valor puesto a mano cuenta solo si es > 0; 0 o vacío = "calcúlalo tú". */
function positive(value: number | null | undefined): number | null {
  return value != null && value > 0 ? value : null;
}
